import { setMemeInfo } from "store/features/memeGenerator/actions";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { useMemeEditor } from "../../MemeEditorContext";
import { toCanvas } from "html-to-image";
import { calculateCanvasImageDimensions } from "client/utils/calculateCanvasImageDimensions";
import { PaddingType } from "store/features/memeGenerator/types/padding";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { downloadImage } from "client/utils/downloadImage";
import { useGoogleAnalytics } from "client/hooks/utils/useGoogleAnalytics";

export const useDownloadButton = () => {
    const isDownloading = !!useAppSelector((state) => state.memeGenerator.meme?.isDownloading);
    const { shadowItemsContainerRef } = useMemeEditor();
    const dispatch = useAppDispatch();
    const { gtag } = useGoogleAnalytics(true);

    async function handleClick() {
        if (isDownloading) return;
        const meme = store.getState().memeGenerator.meme;
        if (!meme) throw new Error(`Tried to download meme when meme is ${meme}`);
        gtag("event", "meme_downloaded", {
            page: "meme generator",
            downloaded_meme: meme.template.slugKey,
        });
        dispatch(
            setMemeInfo({
                isDownloading: true,
            }),
        );
        const itemsContainer = shadowItemsContainerRef?.current;
        if (!itemsContainer) {
            dispatch(
                setMemeInfo({
                    isDownloading: false,
                }),
            );
            return;
        }
        // load all necessary fonts to @font-face
        meme.textBoxes.forEach((textBox) => {
            const font = store
                .getState()
                .memeGenerator.googleFonts.list.find((font) => font.family === textBox.fontFamily);
            if (!font) return;
            return loadFont(font.family, font.files[300] || Object.values(font.files)[0]);
        });
        const { width, height } = calculateCanvasImageDimensions(
            meme.template.settings.width,
            meme.template.settings.height +
                (meme.padding.type === PaddingType.NONE || meme.padding.instances.length === 0
                    ? 0
                    : meme.padding.type === PaddingType.TOP || meme.padding.type === PaddingType.BOTTOM
                    ? (meme.padding.value * meme.template.settings.height) / 100
                    : (meme.padding.value * meme.template.settings.height * 2) / 100),
            Math.max(meme.template.settings.width, meme.settings.image.width),
            999999999999,
            false,
        );
        const [xRatio, yRatio] = [
            width / meme.settings.itemsContainer.width,
            height / meme.settings.itemsContainer.height,
        ];
        const imageSettings = {
            left: meme.settings.image.left * xRatio,
            top: meme.settings.image.top * yRatio,
            width: meme.settings.image.width * xRatio,
            height: meme.settings.image.height * yRatio,
        };
        const isLocal = meme.template.siteUrl === "local";
        const topCanvas = await toCanvas(itemsContainer, {
            cacheBust: isLocal ? undefined : true,
            pixelRatio: window.devicePixelRatio,
        });
        const url = new URL(meme.template.imageUrl);
        url.searchParams.set("cacheBust", `${new Date().getTime()}`);
        const { src } = isLocal ? { src: meme.template.imageUrl } : await downloadImage(url.toString(), 0);
        const { image } = await loadImage(src);
        const finalCanvas = document.createElement("canvas");
        finalCanvas.width = width;
        finalCanvas.height = height;
        finalCanvas.style.width = `${width * window.devicePixelRatio}px`;
        finalCanvas.style.height = `${height * window.devicePixelRatio}px`;
        const ctx = finalCanvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(image, imageSettings.left, imageSettings.top, imageSettings.width, imageSettings.height);
        ctx.drawImage(topCanvas, 0, 0, width, height);
        const link = document.createElement("a");
        link.download = `${meme.template.title.split(".")[0]}.png`;
        link.href = finalCanvas.toDataURL("image/png");
        link.click();
        link.remove();
        dispatch(
            setMemeInfo({
                isDownloading: false,
            }),
        );
    }

    function loadFont(fontName: string, urlToFontFile: string) {
        const style = document.createElement("style");
        style.textContent = `
          @font-face {
            font-family: '${fontName}';
            src: url(${urlToFontFile});
          }
        `;
        document.head.appendChild(style);
    }

    return {
        handleClick,
        isDownloading,
    };
};
